import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const DEV_CHAT_PORT = import.meta.env.VITE_CHAT_WS_DEV_PORT || 8787;
const CHAT_WS_URL = import.meta.env.VITE_CHAT_WS_URL?.trim();

const ensureStoredValue = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;
  window.localStorage.setItem(key, fallback);
  return fallback;
};

const resolveWsBase = () => {
  if (CHAT_WS_URL) {
    const isLocalOverride = /localhost|127\.0\.0\.1/i.test(CHAT_WS_URL);
    if (typeof window !== 'undefined' && !isLocalOverride) {
      return CHAT_WS_URL;
    }
    if (typeof window === 'undefined') {
      return CHAT_WS_URL;
    }
  }

  if (typeof window === 'undefined') {
    return `ws://localhost:${DEV_CHAT_PORT}`;
  }

  const { protocol, hostname, port } = window.location;
  const scheme = protocol === 'https:' ? 'wss' : 'ws';
  if (port && ['5173', '4173', '3000', '5174'].includes(port)) {
    return `${scheme}://${hostname}:${DEV_CHAT_PORT}`;
  }
  if (port) return `${scheme}://${hostname}:${port}`;
  return `${scheme}://${hostname}`;
};

const buildWsUrl = (params) => {
  const base = resolveWsBase().replace(/\/$/, '');
  const query = new URLSearchParams(params).toString();
  return `${base}/ws?${query}`;
};

const createPageId = (prefix) => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const dedupeMessages = (items) => {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).filter((item) => {
    if (!item || !item.id) return false;
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const formatTime = (ts) => {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const roleLabel = (role) => {
  switch (role) {
    case 'agent':
      return 'Corpusio';
    case 'bot':
      return 'Bot';
    case 'system':
      return 'System';
    default:
      return 'You';
  }
};

const baseButtonStyle = {
  border: 'none',
  borderRadius: '999px',
  cursor: 'pointer',
  fontWeight: 600,
  transition: 'opacity 0.2s ease',
};

function ChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [unread, setUnread] = useState(0);
  const [launcherBottom, setLauncherBottom] = useState(32);

  const viewportRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const openRef = useRef(open);
  const roomIdRef = useRef(createPageId('room'));
  const sessionIdRef = useRef(createPageId('session'));
  const launcherDragRef = useRef({ active: false, startY: 0, startBottom: 32, moved: false });

  const displayName = useMemo(() => ensureStoredValue('corpusio_chat_name', 'Guest'), []);

  const hiddenOnDashboard = location.pathname.startsWith('/dashboard');
  useEffect(() => {
    if (hiddenOnDashboard && open) {
      setOpen(false);
    }
  }, [hiddenOnDashboard, open]);

  const scrollToBottom = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const appendMessage = useCallback(
    (msg) => {
      let added = false;
      setMessages((prev) => {
        if (!msg?.id || prev.some((item) => item.id === msg.id)) return prev;
        added = true;
        return [...prev, msg].slice(-80);
      });
      if (!added) return;
      if (!openRef.current) {
        setUnread((prev) => prev + 1);
      } else {
        setTimeout(scrollToBottom, 0);
      }
    },
    [scrollToBottom]
  );

  useEffect(() => {
    openRef.current = open;
    if (open) {
      setUnread(0);
      setTimeout(scrollToBottom, 0);
    }
  }, [open, scrollToBottom]);

  useEffect(() => {
    if (openRef.current) {
      setTimeout(scrollToBottom, 0);
    }
  }, [messages.length, scrollToBottom]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;

    const cleanup = () => {
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };

    const scheduleReconnect = (delay = 3000) => {
      if (cancelled) return;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      reconnectTimer.current = setTimeout(connect, delay);
    };

    const connect = () => {
      if (cancelled) return;
      cleanup();
      const preferredLang = typeof navigator !== 'undefined'
        ? (navigator.languages && navigator.languages[0]) || navigator.language || 'en'
        : 'en';
      const wsUrl = buildWsUrl({
        room: roomIdRef.current,
        session: sessionIdRef.current,
        name: displayName,
        role: 'user',
        lang: preferredLang,
      });
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        if (cancelled) return;
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === 'history' && Array.isArray(payload.items)) {
            setMessages(dedupeMessages(payload.items).slice(-80));
            if (openRef.current) setUnread(0);
          } else if (payload.type === 'message' && payload.msg) {
            appendMessage(payload.msg);
          } else if (payload.type === 'system' && payload.text) {
            appendMessage({
              id: `system-${Date.now()}`,
              role: 'system',
              text: payload.text,
              ts: Date.now(),
            });
          }
        } catch {
          // ignore malformed payloads
        }
      };

      ws.onerror = () => {
        if (cancelled) return;
        setError('Chat is temporarily unavailable.');
      };

      ws.onclose = () => {
        if (cancelled) return;
        setConnected(false);
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
      cleanup();
    };
  }, [appendMessage, displayName]);

  const sendMessage = useCallback(
    (event) => {
      event?.preventDefault?.();
      const text = input.trim();
      if (!text) return;
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        setError('Chat is offline. Please try again in a moment.');
        return;
      }
      ws.send(JSON.stringify({ type: 'message', id: createPageId('msg'), text }));
      setInput('');
    },
    [input]
  );

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage(event);
    }
  };

  if (hiddenOnDashboard) {
    return null;
  }

  const toggleWidget = () => {
    if (launcherDragRef.current.moved) {
      launcherDragRef.current.moved = false;
      return;
    }
    setOpen((prev) => !prev);
  };

  const clampLauncherBottom = (value) => {
    if (typeof window === 'undefined') return value;
    const max = Math.max(32, window.innerHeight - 96);
    return Math.min(Math.max(value, 24), max);
  };

  const handleLauncherPointerDown = (event) => {
    launcherDragRef.current = {
      active: true,
      startY: event.clientY,
      startBottom: launcherBottom,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleLauncherPointerMove = (event) => {
    const drag = launcherDragRef.current;
    if (!drag.active) return;
    const delta = drag.startY - event.clientY;
    if (Math.abs(delta) > 4) drag.moved = true;
    setLauncherBottom(clampLauncherBottom(drag.startBottom + delta));
  };

  const handleLauncherPointerUp = (event) => {
    launcherDragRef.current.active = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const panelStyle = {
    position: 'fixed',
    bottom: '96px',
    right: '32px',
    width: '360px',
    height: 'min(680px, calc(100vh - 128px))',
    maxHeight: 'calc(100vh - 128px)',
    maxWidth: 'calc(100vw - 32px)',
    background: '#FFFFFF',
    borderRadius: '20px',
    boxShadow: '0 25px 65px rgba(15, 23, 42, 0.25)',
    display: open ? 'flex' : 'none',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    zIndex: 1100,
  };

  const headerStyle = {
    padding: '16px 20px',
    borderBottom: '1px solid #E2E8F0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#1F2937',
    color: '#FFFFFF',
    flexShrink: 0,
  };

  const messageListStyle = {
    flex: 1,
    minHeight: 0,
    padding: '16px',
    overflowY: 'auto',
    background: '#F8FAFC',
    display: 'flex',
    flexDirection: 'column',
  };

  const messageBubble = (role) => ({
    alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
    background: role === 'user' ? '#3457D5' : '#E2E8F0',
    color: role === 'user' ? '#FFFFFF' : '#1E293B',
    borderRadius: '16px',
    padding: '10px 14px',
    marginBottom: '10px',
    maxWidth: '85%',
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)',
  });

  const footerStyle = {
    borderTop: '1px solid #E2E8F0',
    padding: '12px',
    background: '#FFFFFF',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'flex-end',
    gap: '10px',
  };

  const launcherStyle = {
    position: 'fixed',
    bottom: `${launcherBottom}px`,
    right: '32px',
    width: '64px',
    height: '64px',
    background: '#3457D5',
    color: '#FFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '26px',
    ...baseButtonStyle,
    zIndex: 1050,
    boxShadow: '0 15px 35px rgba(52, 87, 213, 0.35)',
  };

  const badgeStyle = {
    position: 'absolute',
    top: '-6px',
    right: '-6px',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: '#F97316',
    color: '#FFF',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    boxShadow: '0 8px 20px rgba(249, 115, 22, 0.4)',
  };

  return (
    <>
      <button
        type="button"
        style={launcherStyle}
        onClick={toggleWidget}
        onPointerDown={handleLauncherPointerDown}
        onPointerMove={handleLauncherPointerMove}
        onPointerUp={handleLauncherPointerUp}
        onPointerCancel={handleLauncherPointerUp}
        aria-label="Chat with Corpusio"
        title="Drag up or down"
      >
        {open ? '×' : '💬'}
        {!open && unread > 0 && <span style={badgeStyle}>{unread}</span>}
      </button>
      <div style={panelStyle} aria-live="polite" aria-label="Corpusio chat assistant">
          <div style={headerStyle}>
              <div>
                  <div style={{ fontWeight: 700 }}>Corpusio Assistant</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>
                      {connected ? 'Online and ready to help' : 'Connecting…'}
                  </div>
              </div>
              <button
                  type="button"
                  onClick={toggleWidget}
                  style={{ ...baseButtonStyle, background: 'transparent', color: '#F8FAFC', fontSize: '24px', width: '32px', height: '32px' }}
                  aria-label="Close chat widget"
              >
                  ×
              </button>
          </div>
          <div ref={viewportRef} style={messageListStyle}>
              {messages.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#475569', fontSize: '14px', marginTop: '40px' }}>
                      {connected ? 'Ask anything about Corpusio and we’ll reply instantly.' : 'Bringing the assistant online…'}
                  </div>
              )}
              {messages.map((msg) => (
                  <div key={msg.id} style={messageBubble(msg.role)}>
                      <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>
                          {roleLabel(msg.role)} · {formatTime(msg.ts)}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                  </div>
              ))}
              {error && (
                  <div style={{ marginTop: '12px', fontSize: '12px', color: '#DC2626' }}>
                      {error}
                  </div>
              )}
          </div>
          <form style={footerStyle} onSubmit={sendMessage}>
              <textarea
                  rows={1}
                  style={{
                      flex: 1,
                      minWidth: 0,
                      minHeight: '44px',
                      maxHeight: '96px',
                      borderRadius: '12px',
                      border: '1px solid #CBD5F5',
                      padding: '11px 12px',
                      resize: 'none',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      lineHeight: 1.45,
                  }}
                  placeholder="Ask about plans, security, docs…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={!connected}
              />
              <button
                  type="submit"
                  style={{
                      ...baseButtonStyle,
                      width: '44px',
                      height: '44px',
                      flex: '0 0 44px',
                      padding: 0,
                      background: connected ? '#3457D5' : '#A5B4FC',
                      color: '#FFF',
                      fontSize: '22px',
                      lineHeight: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                  }}
                  aria-label="Send message"
                  disabled={!connected || !input.trim()}
              >
                  →
              </button>
          </form>
      </div>
    </>
  );
}

export default ChatWidget;
